import { supabase } from "@/ajax/clients/supabase";
import { Capacitor } from "@capacitor/core";

/**
 * Helpers around Supabase MFA for our 2FA enforcement.
 *
 * Allowed second factors: TOTP (authenticator app) and passkey/WebAuthn.
 * SMS/phone is intentionally NOT supported here, and is also disabled at the
 * Supabase project level. See docs/two-factor-auth-enforcement-design.md.
 *
 * The real security boundary is the API (it rejects first-factor-only sessions
 * for mfa-required users). These helpers drive the UI that gets a user from a
 * first-factor-only session to one with a verified second factor.
 */

export type SecondFactorType = "totp" | "webauthn";

export type MfaFactor = {
  id: string;
  factorType: SecondFactorType;
  friendlyName?: string;
  createdAt: string;
};

export type MfaStatus = {
  /** Whether THIS session has completed a verified second factor. */
  secondFactorVerified: boolean;
  /** Verified TOTP/passkey factors attached to the user. */
  verifiedFactors: MfaFactor[];
};

/**
 * Whether the current session has completed its second factor. This is the one
 * place that reads Supabase's raw `aal` value; everywhere else uses the
 * boolean. Fails closed (returns false) if the level can't be determined, so a
 * transient error sends the user to re-verify rather than slipping through.
 */
export const getSecondFactorVerified = async (): Promise<boolean> => {
  const { data, error } =
    await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) {
    return false;
  }
  return data.currentLevel === "aal2";
};

/** Browsers without WebAuthn can't enroll/use passkeys. */
const browserSupportsPasskey = () =>
  typeof window !== "undefined" &&
  typeof window.PublicKeyCredential !== "undefined";

/**
 * Whether passkeys (WebAuthn) can be used in the current environment.
 *
 * Passkeys are offered on web only. Inside the Capacitor native WebView the
 * WebAuthn origin is `capacitor://localhost`, which Supabase rejects ("invalid
 * RP origins"), and native passkey APIs (iOS AuthenticationServices / Android
 * Credential Manager) aren't wired through the WebView. So on native we hide
 * passkeys and use TOTP instead.
 */
export const isPasskeySupported = () =>
  browserSupportsPasskey() && !Capacitor.isNativePlatform();

/**
 * Reads the current assurance level and the user's verified second factors.
 * Only TOTP and WebAuthn factors are surfaced; any phone factors (which should
 * never exist given platform config) are ignored.
 */
export const getMfaStatus = async (): Promise<MfaStatus> => {
  const [aal, factors] = await Promise.all([
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
    supabase.auth.mfa.listFactors(),
  ]);

  if (aal.error) {
    throw aal.error;
  }
  if (factors.error) {
    throw factors.error;
  }

  const verifiedFactors: MfaFactor[] = [
    ...(factors.data.totp ?? []),
    ...(factors.data.webauthn ?? []),
  ]
    .filter((f) => f.status === "verified")
    .map((f) => ({
      id: f.id,
      factorType: f.factor_type as SecondFactorType,
      friendlyName: f.friendly_name,
      createdAt: f.created_at,
    }));

  return {
    secondFactorVerified: aal.data.currentLevel === "aal2",
    verifiedFactors,
  };
};

/**
 * Removes any leftover unverified factors. Supabase rejects enrolling a second
 * factor of the same type while an unverified one lingers (e.g. the user
 * abandoned a previous attempt), so we clean those up before re-enrolling.
 */
const removeUnverifiedFactors = async () => {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) {
    return;
  } // best-effort cleanup
  const stale = data.all.filter((f) => f.status === "unverified");
  await Promise.all(
    stale.map((f) => supabase.auth.mfa.unenroll({ factorId: f.id })),
  );
};

export type TotpEnrollment = {
  factorId: string;
  /** SVG data-URI QR code to scan into an authenticator app. */
  qrCode: string;
  /** Text secret for manual entry. */
  secret: string;
};

/** Begins TOTP enrollment. Present the QR code/secret, then call verifyTotp. */
export const enrollTotp = async (): Promise<TotpEnrollment> => {
  await removeUnverifiedFactors();
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    friendlyName: `Authenticator (${new Date().toISOString()})`,
  });
  if (error) {
    throw error;
  }
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  };
};

/**
 * Verifies the code from the authenticator app, completing enrollment OR an
 * ongoing challenge. On success Supabase verifies the second factor for the session.
 */
export const verifyTotp = async (factorId: string, code: string) => {
  const { error } = await supabase.auth.mfa.challengeAndVerify({
    factorId,
    code: code.trim(),
  });
  if (error) {
    throw error;
  }
};

/**
 * Full passkey enrollment in one step: enroll + WebAuthn create ceremony +
 * verify. On success the second factor is verified for the session.
 */
export const enrollPasskey = async () => {
  await removeUnverifiedFactors();
  const { error } = await supabase.auth.mfa.webauthn.register({
    friendlyName: `Passkey (${new Date().toISOString()})`,
  });
  if (error) {
    throw error;
  }
};

/**
 * Runs the passkey challenge (WebAuthn get ceremony + verify) against an
 * already-enrolled factor. On success the second factor is verified for the session.
 */
export const challengePasskey = async (factorId: string) => {
  const { error } = await supabase.auth.mfa.webauthn.authenticate({ factorId });
  if (error) {
    throw error;
  }
};

/**
 * Removes (unenrolls) a second-factor device. Note: removing the last verified
 * factor downgrades the session to first-factor-only, so callers that enforce
 * 2FA should prevent removing the last device (or re-route to setup).
 */
export const removeMfaDevice = async (factorId: string) => {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) {
    throw error;
  }
};
